/**
 * NotificationDropdown Component
 *
 * Displays a dropdown panel with notification list, tabs, and actions.
 */

import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Button,
    CircularProgress,
    Tabs,
    Tab,
    Divider,
    Paper,
    ClickAwayListener,
    Popper,
    Fade,
} from '@mui/material';
import {
    NotificationsOff as NotificationsOffIcon,
    DoneAll as DoneAllIcon,
} from '@mui/icons-material';
import NotificationItem from './NotificationItem';
import { useNavigate } from 'react-router-dom';

const CATEGORIES = [
    { value: null, label: 'All' },
    { value: 'PAYMENT', label: 'Payments' },
    { value: 'LEASE', label: 'Leases' },
    { value: 'MAINTENANCE', label: 'Maintenance' },
    { value: 'TENANT', label: 'Tenants' },
];

const NotificationDropdown = ({
    open,
    anchorEl,
    onClose,
    notifications,
    unreadCount,
    loading,
    onFetchNotifications,
    onMarkAsRead,
    onMarkAllAsRead,
    onDelete,
}) => {
    const [selectedCategory, setSelectedCategory] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        if (open) {
            onFetchNotifications(1, selectedCategory);
        }
    }, [open, selectedCategory, onFetchNotifications]);

    const handleCategoryChange = (event, newValue) => {
        setSelectedCategory(newValue);
    };

    const handleNotificationClick = (notification, actionUrl) => {
        if (actionUrl) {
            onClose();
            navigate(actionUrl);
        }
    };

    const handleMarkAllAsRead = () => {
        onMarkAllAsRead(selectedCategory);
    };

    const handleViewAll = () => {
        onClose();
        navigate('/notifications');
    };

    return (
        <Popper
            open={open}
            anchorEl={anchorEl}
            placement="bottom-end"
            transition
            style={{ zIndex: 1300 }}
        >
            {({ TransitionProps }) => (
                <Fade {...TransitionProps} timeout={200}>
                    <Paper
                        elevation={8}
                        sx={{
                            width: 380,
                            maxHeight: 520,
                            mt: 1,
                            overflow: 'hidden',
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: 'divider',
                        }}
                    >
                        <ClickAwayListener onClickAway={onClose}>
                            <Box>
                                {/* Header */}
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        px: 2,
                                        py: 1.5,
                                        borderBottom: '1px solid',
                                        borderColor: 'divider',
                                    }}
                                >
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                        Notifications
                                        {unreadCount > 0 && (
                                            <Typography
                                                component="span"
                                                sx={{
                                                    ml: 1,
                                                    px: 1,
                                                    py: 0.25,
                                                    borderRadius: 10,
                                                    bgcolor: '#ef4444',
                                                    color: 'white',
                                                    fontSize: '0.75rem',
                                                    fontWeight: 600,
                                                }}
                                            >
                                                {unreadCount > 99 ? '99+' : unreadCount}
                                            </Typography>
                                        )}
                                    </Typography>
                                    {unreadCount > 0 && (
                                        <Button
                                            size="small"
                                            startIcon={<DoneAllIcon sx={{ fontSize: 16 }} />}
                                            onClick={handleMarkAllAsRead}
                                            sx={{
                                                textTransform: 'none',
                                                fontSize: '0.8125rem',
                                            }}
                                        >
                                            Mark all read
                                        </Button>
                                    )}
                                </Box>

                                {/* Category Tabs */}
                                <Tabs
                                    value={selectedCategory}
                                    onChange={handleCategoryChange}
                                    variant="scrollable"
                                    scrollButtons="auto"
                                    sx={{
                                        minHeight: 36,
                                        borderBottom: '1px solid',
                                        borderColor: 'divider',
                                        '& .MuiTab-root': {
                                            minHeight: 36,
                                            py: 0,
                                            px: 1.5,
                                            fontSize: '0.8125rem',
                                            textTransform: 'none',
                                        },
                                    }}
                                >
                                    {CATEGORIES.map((cat) => (
                                        <Tab key={cat.label} value={cat.value} label={cat.label} />
                                    ))}
                                </Tabs>

                                {/* Notification List */}
                                <Box
                                    sx={{
                                        maxHeight: 350,
                                        overflowY: 'auto',
                                        '&::-webkit-scrollbar': {
                                            width: 6,
                                        },
                                        '&::-webkit-scrollbar-thumb': {
                                            bgcolor: 'rgba(0,0,0,0.2)',
                                            borderRadius: 3,
                                        },
                                    }}
                                >
                                    {loading ? (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                py: 4,
                                            }}
                                        >
                                            <CircularProgress size={24} />
                                        </Box>
                                    ) : notifications.length === 0 ? (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                py: 5,
                                                color: '#9ca3af',
                                            }}
                                        >
                                            <NotificationsOffIcon sx={{ fontSize: 48, mb: 1 }} />
                                            <Typography variant="body2">No notifications yet</Typography>
                                        </Box>
                                    ) : (
                                        notifications.map((notification) => (
                                            <NotificationItem
                                                key={notification.notification_id}
                                                notification={notification}
                                                onMarkAsRead={onMarkAsRead}
                                                onDelete={onDelete}
                                                onClick={handleNotificationClick}
                                            />
                                        ))
                                    )}
                                </Box>

                                {/* Footer */}
                                <Divider />
                                <Box
                                    sx={{
                                        px: 2,
                                        py: 1.5,
                                        textAlign: 'center',
                                    }}
                                >
                                    <Button
                                        size="small"
                                        onClick={handleViewAll}
                                        sx={{
                                            textTransform: 'none',
                                            fontSize: '0.875rem',
                                        }}
                                    >
                                        View all notifications
                                    </Button>
                                </Box>
                            </Box>
                        </ClickAwayListener>
                    </Paper>
                </Fade>
            )}
        </Popper>
    );
};

export default NotificationDropdown;
